import {
  COMEDYUO_EMAIL,
  COMEDYUO_INSTAGRAM_URL,
  COMEDYUO_YOUTUBE_URL,
  DEFAULT_OG_IMAGE_URL,
  absolutePublicUrl,
} from "@/app/_utils/constants";

export const SITE_ORIGIN = "https://comedyuo.com";
export const ORGANIZATION_ID = `${SITE_ORIGIN}/#organization`;
export const WEBSITE_ID = `${SITE_ORIGIN}/#website`;

export const ORGANIZATION_NAME = "Underground Overground Comedy";
export const ORGANIZATION_ALTERNATE_NAMES = ["ComedyUO", "Comedy UO"];

export function organizationRef() {
  return { "@id": ORGANIZATION_ID };
}

export function organizationSchema() {
  return {
    "@type": "Organization",
    "@id": ORGANIZATION_ID,
    name: ORGANIZATION_NAME,
    alternateName: ORGANIZATION_ALTERNATE_NAMES,
    url: `${SITE_ORIGIN}/`,
    logo: DEFAULT_OG_IMAGE_URL,
    email: COMEDYUO_EMAIL,
    description:
      "Underground Overground Comedy produces pop-up comedy and magic shows in New York City's most iconic spaces.",
    areaServed: { "@type": "City", name: "New York City" },
    sameAs: [COMEDYUO_INSTAGRAM_URL, COMEDYUO_YOUTUBE_URL],
    knowsAbout: [
      "Pop-up comedy shows in New York City",
      "Unique comedy shows in NYC",
      "Comedy in iconic New York locations",
      "Alternatives to traditional comedy clubs",
      "Things to do in NYC at night",
      "Private and corporate comedy events",
      "Private event magicians in NYC",
      "Corporate entertainment in New York City",
    ],
  };
}

export function websiteSchema() {
  return {
    "@type": "WebSite",
    "@id": WEBSITE_ID,
    name: ORGANIZATION_NAME,
    alternateName: ORGANIZATION_ALTERNATE_NAMES,
    url: `${SITE_ORIGIN}/`,
    inLanguage: "en-US",
    publisher: organizationRef(),
  };
}

export function breadcrumbSchema(items: { name: string; path: string }[]) {
  return {
    "@type": "BreadcrumbList",
    itemListElement: items.map((item, index) => ({
      "@type": "ListItem",
      position: index + 1,
      name: item.name,
      item: absolutePublicUrl(item.path),
    })),
  };
}

export function serviceSchema(opts: {
  name: string;
  description: string;
  url: string;
  serviceType: string;
}) {
  return {
    "@type": "Service",
    name: opts.name,
    serviceType: opts.serviceType,
    description: opts.description,
    url: opts.url,
    provider: organizationRef(),
    areaServed: { "@type": "City", name: "New York City" },
  };
}

type EventInput = {
  name: string;
  description?: string | null;
  url: string;
  image?: string | null;
  startDate?: string | null;
  endDate?: string | null;
  locationName?: string | null;
  price?: number | null;
};

export function eventSchema(input: EventInput) {
  const event: Record<string, unknown> = {
    "@type": "Event",
    name: input.name,
    url: input.url,
    eventAttendanceMode: "https://schema.org/OfflineEventAttendanceMode",
    eventStatus: "https://schema.org/EventScheduled",
    organizer: organizationRef(),
  };
  const description = String(input.description ?? "").replace(/\s+/g, " ").trim();
  if (description) event.description = description;
  if (input.image) event.image = input.image;
  if (input.startDate) event.startDate = input.startDate;
  if (input.endDate) event.endDate = input.endDate;
  if (input.locationName) {
    event.location = {
      "@type": "Place",
      name: input.locationName,
      address: {
        "@type": "PostalAddress",
        addressLocality: "New York",
        addressRegion: "NY",
        addressCountry: "US",
      },
    };
  }
  if (typeof input.price === "number" && Number.isFinite(input.price) && input.price > 0) {
    event.offers = {
      "@type": "Offer",
      url: input.url,
      price: input.price,
      priceCurrency: "USD",
    };
  }
  return event;
}

export function jsonLdGraph(nodes: Record<string, unknown>[]) {
  return {
    "@context": "https://schema.org",
    "@graph": nodes,
  };
}
